import React, { FC, useState, useRef, useId, useEffect } from 'react';
import { EditableComponent } from '@adobe/aem-react-editable-components';
import clsx from 'clsx';
import {
  Button,
  Eyebrow,
  EyebrowEnum,
  Heading,
  headingType,
  size,
  Image,
  tags,
  useCheckBreakpoint,
  useWindowSize,
  getIconClassFromToken,
  type TrackingPropsInterface,
} from '@marriott/mi-ui-library';
import { AIcon, AIconSize, MLogoContainer, MLogoContainerBackgroundColor, MTagType } from '@marriott/mi-web-ui-kit';
import { ProductDetailsCardProps } from './ProductDetailsCard.types';
import {
  StyledProductDetailsCard,
  StyledEyebrow,
  StyledTitleRow,
  StyledTags,
  TagsWrapper,
  StyledBadge,
} from './ProductDetailsCard.styles';
import { useImageLoadTracker } from '../../hooks';

function isDcaMerchProductDetailsCard(tp?: TrackingPropsInterface): boolean {
  const v = tp?.isDCAEnabled as boolean | string | undefined;
  return v === true || v === 'true';
}

const ProductDetailsCardComp: FC<ProductDetailsCardProps> = ({
  className,
  componentId,
  styleclass,
  cardVariation,
  cardUrl,
  openInaNewTab,
  icon,
  eyebrow,
  badgeIcon,
  badgeText,
  title,
  tagListItems,
  subTitle,
  description,
  brandLogoIcon,
  fileReferenceImage,
  imageAltText,
  dynamicMedia,
  dynamicMediaTall,
  dynamicMediaWide,
  trackingProperties,
  breakPointKey = 'viewportXl', // default value for desktop is greater than 1200
  isSlotLoading = false,
  onImageLoad,
  onImageError,
}) => {
  const pdcRef = useRef<HTMLDivElement>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  // Generate unique ids to prevent screenreaders from reading only the first card
  const id = useId();
  const renderedTagListItems = badgeText ? tagListItems?.slice(0, 1) : tagListItems;
  const renderTags = (tagListItems: { tagLabel: string }[] | undefined) =>
    tagListItems &&
    tagListItems.length > 0 && (
      <StyledTags id={`${id}tags`} role="list">
        {renderedTagListItems?.map(tag => (
          <li key={tag.tagLabel} role="listitem" {...(!isDesktopViewPort && { tabIndex: 0 })}>
            {tag.tagLabel}
          </li>
        ))}
      </StyledTags>
    );

  const isDesktopViewPort = useCheckBreakpoint(breakPointKey); //is the view port greater than 1200px or 992px
  const { width: viewportWidth } = useWindowSize();
  // making sure the viewport width is defined
  const hasResolvedViewport = typeof viewportWidth === 'number';
  const isPageLoading = !hasResolvedViewport;
  const isDcaSlot = isDcaMerchProductDetailsCard(trackingProperties);
  // A slot is "loading" (shows the skeleton) either because it is a DCA slot
  // awaiting Target content, or any slot flagged as awaiting its async content
  // (e.g. a geo-cf slot awaiting its region CF).
  const isLoadingSlot = isDcaSlot || isSlotLoading;
  let imageRendition = dynamicMedia;
  if (cardVariation === 'tall' && dynamicMediaTall && isDesktopViewPort) {
    imageRendition = dynamicMediaTall;
  } else if (cardVariation === 'wide' && dynamicMediaWide && isDesktopViewPort) {
    imageRendition = dynamicMediaWide;
  }
  const isBrandToken = !!brandLogoIcon?.startsWith('mcom-common:brands/');
  const brand = isBrandToken ? brandLogoIcon?.split('/').pop() : '';
  const brandLogoIconPath =
    isBrandToken && brand
      ? `https://www.dev-cdn.marriott.com/mi-assets/mi-global/brand-logos/${brand}.svg`
      : brandLogoIcon;
  const isPendingDcaTracking =
    !!trackingProperties?.isDCAEnabled &&
    !trackingProperties?.trackingContentPosition &&
    !trackingProperties?.trackingDescription &&
    !trackingProperties?.trackingOfferType &&
    !trackingProperties?.trackingTag &&
    !trackingProperties?.atCCeVar48;

  useEffect(() => {
    /*
      For mobile and tablet accesibility, Setting the focus to subtitle on the click of expand button
      Since the subitle appears earlier in the DOM, So focus is set manually via ref and tabIndex -1
    */
    if (isExpanded) {
      if (pdcRef.current) {
        pdcRef.current?.focus();
      }
    }
  }, [isExpanded]);

  const isImageLoaded = useImageLoadTracker(pdcRef, [
    hasResolvedViewport,
    fileReferenceImage,
    dynamicMedia,
    dynamicMediaTall,
    dynamicMediaWide,
    isDesktopViewPort,
  ]);
  // True once the API response has populated either a default URL or rendition entries.
  // For DCA slots the initial render has neither (normalizer returns '' / EMPTY_RENDITIONS),
  // so we use this flag to keep the skeleton mounted instead of rendering an empty <picture>
  // (which the browser paints as the OS broken-image glyph between skeleton removal and src arrival).
  const hasImageData = Boolean(fileReferenceImage) || Boolean(imageRendition?.renditions?.length);
  // For loading slots (DCA / geo-cf), "loading" must persist until the API has produced image data AND the asset has decoded.
  // Without `!hasImageData` the skeleton flips off as soon as it sees zero <img>
  const isCardLoading = isPageLoading || (isLoadingSlot && !hasImageData) || !isImageLoaded;
  // Loading tiles: keep copy/chrome gated until viewport + image (avoid layout flash).
  // show authored body immediately; image can still load progressively without hiding text.
  const deferContentForAssetLoading = isLoadingSlot && isCardLoading;

  const toggleExpand = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsExpanded(!isExpanded);
  };
  const descriptionId = description ? `${id}description` : undefined;

  return (
    <StyledProductDetailsCard
      $isExpanded={isExpanded}
      cardVariation={cardVariation}
      breakPointKey={breakPointKey}
      className={clsx(
        `pdc-${cardVariation} inverse`,
        isLoadingSlot && (!hasImageData || !isImageLoaded) && 'skeleton-loader',
        deferContentForAssetLoading && 'page-loading',
        className,
        styleclass
      )}
      data-testid="homepage-ProductDetailsCard"
      data-component-name="o-homepage-ProductDetailsCard"
      id={componentId}
    >
      <Button
        key={
          isPendingDcaTracking
            ? 'pending-dca-tracking'
            : `${trackingProperties?.trackingContentPosition || cardUrl}|${
                trackingProperties?.trackingDescription || ''
              }`
        }
        isLink
        href={cardUrl}
        target={openInaNewTab ? '_blank' : '_self'}
        linkType={openInaNewTab ? 'external' : 'internal'}
        className={clsx('pdc-card', { 'is-expanded': !isDesktopViewPort && isExpanded })}
        rel={openInaNewTab ? 'noopener noreferrer' : undefined}
        trackingProperties={trackingProperties}
        ref={pdcRef}
      >
        {(hasResolvedViewport || !isLoadingSlot) && hasImageData ? (
          <Image
            altText={imageAltText}
            renditions={imageRendition?.renditions}
            defaultImageURL={fileReferenceImage}
            customClass="pdc-image"
            onLoad={onImageLoad}
            onError={onImageError}
          />
        ) : null}
        {!deferContentForAssetLoading && (
          <div className="pdc-content">
            <div className="pdc-content-top">
              {eyebrow && (
                <StyledEyebrow id={`${id}eyebrow`}>
                  {icon ? <AIcon icon={getIconClassFromToken(icon)} size={AIconSize.XS} /> : null}
                  <Eyebrow text={eyebrow} variation={EyebrowEnum.Overlay} />
                </StyledEyebrow>
              )}
              {title && (
                <StyledTitleRow id={`${id}title`}>
                  <Heading
                    variation={headingType.subtitle}
                    titleText={title}
                    fontSize={size.extraLarge}
                    element={tags.paragraph}
                    customClass="m-0 m-ellipsis-2lines"
                  />
                  <AIcon icon="icon-arrow-right" size={AIconSize.L} />
                </StyledTitleRow>
              )}
              {badgeText || tagListItems?.length ? (
                <TagsWrapper>
                  {badgeText && (
                    <StyledBadge
                      type={MTagType.Accent}
                      aLabel={{ text: badgeText.toUpperCase() }}
                      aIcon={badgeIcon ? { icon: getIconClassFromToken(badgeIcon) } : undefined}
                      id={`${id}badge`}
                    />
                  )}
                  {renderTags(tagListItems)}
                </TagsWrapper>
              ) : null}
              {subTitle && (
                <div id={`${id}subtitle`} className="pdc-hovercontent pt-2">
                  <Heading
                    variation={headingType.body}
                    titleText={subTitle}
                    fontSize={size.small}
                    element={tags.paragraph}
                    customClass="m-0"
                  />
                </div>
              )}
              {description && (
                <p id={descriptionId} className="t-font-s pdc-hovercontent m-0 pt-2">
                  {description}
                </p>
              )}
            </div>
            <div className="pdc-content-bottom">
              {brandLogoIcon && (
                <div className="pdc-brand-logo" id={`${id}logo`}>
                  {isBrandToken ? (
                    <MLogoContainer
                      ariaRole="note"
                      alignment="Centered"
                      brand={brand || ''}
                      aspectRatio="3:2"
                      width={78}
                      height={52}
                      logopath={brandLogoIconPath}
                      color="background-aware"
                      backgroundColor={MLogoContainerBackgroundColor.Inverse}
                    />
                  ) : brandLogoIconPath ? (
                    <img
                      src={brandLogoIconPath}
                      alt=""
                      className="pdc-brand-logo-image"
                      data-testid="pdc-brand-logo-image"
                      onError={event => {
                        // Hide broken-image glyph if the brand logo asset fails to load.
                        const img = event.currentTarget;
                        img.style.visibility = 'hidden';
                        img.setAttribute('data-image-error', 'true');
                      }}
                    />
                  ) : null}
                </div>
              )}
            </div>
          </div>
        )}
      </Button>
      {!deferContentForAssetLoading && (
        <button
          type="button"
          className={`pdc-mobile-toggle ${isExpanded ? 'is-expanded' : ''}`}
          aria-expanded={isExpanded}
          aria-label={isExpanded ? 'Close additional information' : 'Expand to see more information'}
          onClick={toggleExpand}
          onMouseDown={e => {
            e.stopPropagation();
          }}
          onTouchStart={e => {
            e.stopPropagation();
          }}
        >
          <span className={`icon-info ${isExpanded ? 'd-none' : 'd-flex'}`}>
            <svg width="3" height="16" viewBox="0 0 3 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect width="3" height="9" transform="translate(0 6.5)" fill="#1C1C1C" />
              <rect width="3" height="3" transform="translate(0 0.5)" fill="#1C1C1C" />
            </svg>
          </span>
          <span className={`icon-clear ${isExpanded ? 'd-flex' : 'd-none'}`} />
        </button>
      )}
    </StyledProductDetailsCard>
  );
};

export const ProductDetailsCard = (props: ProductDetailsCardProps) => {
  return (
    <EditableComponent
      config={{
        emptyLabel: 'Product Details Card',
        isEmpty: () => false,
        resourceType: 'mi-aem-homepage-spa/components/content/productdetailscard',
      }}
      {...props}
    >
      <ProductDetailsCardComp {...props} />
    </EditableComponent>
  );
};
